import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType, ListFilterFieldType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import { renderDate } from 'components/lists/functions/renderListColumn';
import ContractForm from './ContractForm';

// Enable the bulk actions panel for this list, with the terminate custom action
const contractBulkActions: BulkActionsConfig = {
    objectKey: 'contract',
    showExport: true,
    customActions: [
        {
            key: 'terminate',
            label: 'contract.bulk_actions.terminate_label',
            confirmTitle: 'contract.bulk_actions.terminate_confirm_title',
            confirmMessage: 'contract.bulk_actions.terminate_confirm_message',
        },
    ],
};

// Configure the more menu of this list
const contractMoreMenu: ListMoreMenuConfig = {
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
        clearFilterNames: ['status'],
    },
};

// Set the filter fields of this list
const contractFilterFields: ListFilterFieldType[] = [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5
    },
    {
        type: 'dropdown',
        name: 'status',
        label: 'lists.filters.status_label',
        queryParam: 'status',
        backendField: 'id',
        optionField: 'id',
        translationPrefix: 'contract.status.',
        defaultValue: 'active',
        hardcodedOptions: [
            { id: 'active' },
            { id: 'expired' },
            { id: 'cancelled' },
        ],
        width: 1.25
    },
    {
        type: 'daterange',
        name: 'start_date_range',
        label: 'contract.general.start_date_label',
        queryParam: 'start_date',
        width: 2
    },
    {
        type: 'dropdown',
        name: 'division',
        label: 'lists.filters.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5
    }
];

// Rename the name of the function:
const ContractList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_contract_list',
        defaultParams: { 'deleted': false },
        object: 'contract'
    }
    const objectName = 'contract.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const formModal = <ContractForm />;
    const modalSize = 'add-form';
    const onRowClick = 'detailPage'
    const detailPageUrl = 'contracts'

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { width: '1.2fr', field: 'number', label: 'contract.general.number_label' },
        { width: '2fr', field: 'reference', label: 'contract.general.reference_label' },
        { width: '2fr', field: 'client_name', orderField: 'client__name', label: 'contract.general.client_label' },
        { width: '3fr', field: 'work_location_address', orderField: 'work_location__address', label: 'contract.general.work_location_label' },
        { width: '120px', field: 'start_date', label: 'contract.general.start_date_label', render: (_, row) => renderDate(row.start_date, userLocale) },
        { width: '120px', field: 'end_date', label: 'contract.general.end_date_label', render: (_, row) => renderDate(row.end_date, userLocale) },
        {
            width: '144px',
            field: 'status',
            label: 'contract.general.status_label',
            render: (_, row) => (
                <div className='badge-container'>
                    <div className={`status-badge contract-${row.status}`}>
                        {t(`contract.status_name.${row.status}`)}
                    </div>
                </div>
            )
        },
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filterFields={contractFilterFields}
            formModal={formModal}
            modalSize={modalSize}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            detailPageUrl={detailPageUrl}
            bulkActions={contractBulkActions}
            moreMenu={contractMoreMenu}
        />
    );
};

export default ContractList;
